// @ts-check
/**
 * Opening and serving the hub — the two calls an application makes, with
 * the entrypoint URL as the application's to give.
 *
 * The origin's `openHub()` derived the entrypoint's URL from its own
 * `import.meta.url`, which tied the helper file to an entrypoint the
 * application owns. Here `openHub` takes the URL, and `serveHub` is what
 * that entrypoint calls: the whole of it is two lines.
 *
 * ```js
 * // channel-hub-entrypoint.js, the application's
 * import { serveHub } from '@smotaal/platform-orchestration/channels.js';
 * serveHub(globalThis);
 *
 * // the page
 * const { port, kind } = openHub({ url: new URL('./channel-hub-entrypoint.js', import.meta.url) });
 * const channels = ChannelEndpoint.connect({ name: 'page', port });
 * ```
 *
 * @module @smotaal/platform-orchestration/lib/channels/hub
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Fable 5.1 (code generation)
 */

import { ChannelHub } from './channel-hub.js';
import { DEFAULT_HUB_NAME } from './topics.js';

/**
 * Serves a worker scope as the hub, with the top-level error hooks a hub
 * that fails silently would otherwise lack.
 *
 * @param {any} scope `globalThis` in the entrypoint
 * @param {{ label?: string, windowMs?: number, report?: import('./channel-hub.js').Reporter, shared?: boolean }} [options]
 */
export function serveHub(scope, options = {}) {
    const label = options.label ?? '[ChannelHub]';
    const report = options.report ?? ((level, message, detail) => {
        if (level === 'log') return;
        if (detail === undefined) console[level](message);
        else console[level](message, detail);
    });
    if (typeof scope?.addEventListener === 'function') {
        scope.addEventListener('error', (/** @type {any} */ event) => {
            report('error', `${label} UNCAUGHT error — ${event.message} (${event.filename}:${event.lineno})`, event.error);
        });
        scope.addEventListener('unhandledrejection', (/** @type {any} */ event) => {
            report('error', `${label} UNCAUGHT rejection`, event.reason);
        });
    }
    return ChannelHub.serve(scope, { ...options, label, report });
}

/**
 * Opens the origin's hub: the `SharedWorker` at `url` where the browser has
 * one, else a dedicated `Worker` running the same module for this context
 * alone. A `SharedWorker`'s identity is its URL and name, so two pages that
 * open the same URL share one hub.
 *
 * @param {{ url: URL | string, name?: string, dedicated?: boolean, scope?: any }} options
 *   `dedicated` forces the per-context `Worker` — the fallback a page takes
 *   when the shared one failed to start.
 * @returns {{ port: import('../ports/port.js').PortLike, kind: 'SharedWorker' | 'Worker', source: any }}
 */
export function openHub({ url, name = DEFAULT_HUB_NAME, dedicated = false, scope = globalThis }) {
    if (!url) throw new TypeError('openHub needs the URL of the hub entrypoint — the entrypoint is the application\'s');
    const SharedWorkerLike = scope?.SharedWorker;
    const WorkerLike = scope?.Worker;
    if (!dedicated && typeof SharedWorkerLike === 'function') {
        const shared = new SharedWorkerLike(url, { type: 'module', name });
        shared.port.start?.();
        return { port: shared.port, kind: 'SharedWorker', source: shared };
    }
    if (typeof WorkerLike !== 'function') throw new TypeError('This context has neither SharedWorker nor Worker, so no hub can be opened');
    const worker = new WorkerLike(url, { type: 'module' });
    return { port: worker, kind: 'Worker', source: worker };
}
